import { usePageTitle } from "@/hooks/usePageTitle";
import { Link } from "react-router-dom";
import { Compass, Home, LifeBuoy, Search } from "lucide-react";
import { PublicNav } from "@/components/layout/PublicNav";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageMeta } from "@/components/seo/PageMeta";

const NotFound = () => {
  usePageTitle('Page Not Found');

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="Page Not Found"
        description="The page you were looking for does not exist on PropTRUST Navigator."
        url={window.location.href}
      />
      <PublicNav />
      <div className="container flex min-h-[calc(100vh-8rem)] items-center py-24">
        <div className="grid w-full gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div className="space-y-6">
            <div className="inline-flex rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-sm font-medium text-primary">
              404 | Page not found
            </div>
            <div className="space-y-4">
              <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
                This page wandered off the transaction trail.
              </h1>
              <p className="max-w-2xl text-lg leading-8 text-muted-foreground">
                The link may be broken, the page may have moved, or the address might be incorrect.
                Let&apos;s get you back to a live part of PropTRUST.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg">
                <Link to="/">
                  <Home className="mr-2 h-4 w-4" />
                  Back to home
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/features">
                  <LifeBuoy className="mr-2 h-4 w-4" />
                  Explore features
                </Link>
              </Button>
            </div>
          </div>

          <Card className="border-border/70 bg-card/80 shadow-xl">
            <CardContent className="space-y-5 p-6">
              <div className="rounded-3xl bg-muted/40 p-5">
                <Search className="h-6 w-6 text-primary" />
                <h2 className="mt-4 text-xl font-semibold text-foreground">Try one of these instead</h2>
                <div className="mt-4 space-y-3 text-sm text-muted-foreground">
                  <p>Open the dashboard to continue a matter.</p>
                  <p>Browse properties if you were checking a listing.</p>
                  <p>Use the help centre if you expected a specific route.</p>
                </div>
              </div>
              <div className="space-y-3">
                <Button asChild variant="outline" className="w-full justify-start rounded-2xl">
                  <Link to="/pricing">
                    <Compass className="mr-2 h-4 w-4" />
                    Pricing
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start rounded-2xl">
                  <Link to="/features">
                    <Compass className="mr-2 h-4 w-4" />
                    Features
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start rounded-2xl">
                  <Link to="/properties">
                    <Compass className="mr-2 h-4 w-4" />
                    Properties
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
};

export default NotFound;
